import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ObjectStorageProvider } from '@app/shared/interfaces/object-storage.interface';
import {
  grantLocation,
  isValidGcsBucketName,
  MAX_PENDING_STORAGE_GRANTS,
  StorageGrant,
  StorageGrantStatus,
} from '@app/shared/interfaces/storage-grant.interface';
import { StorageGrantService } from '../../shared/services/storage-grant/storage-grant.service';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { ServerDatePipe } from '../../shared/pipes/server-date.pipe';

/** Grants a project can still withdraw. */
const LIVE: StorageGrantStatus[] = ['Pending', 'Approved'];

/**
 * StorageGrantsComponent — a project's storage access (EHQ-427).
 *
 * Keyless GCS connections use Enlighten's own service account, so a project
 * admin asks for a bucket (or a prefix in it) here and an instance admin
 * decides in ehq-admin. Approved grants are what the Keyless option in Add
 * connection offers. Project-admin only, like the server endpoints.
 */
@Component({
  selector: 'app-storage-grants',
  standalone: true,
  imports: [
    ServerDatePipe,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './storage-grants.component.html',
  styleUrls: ['./storage-grants.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StorageGrantsComponent {
  readonly projectId = input<number | null>(null);

  readonly #service = inject(StorageGrantService);
  readonly #dialogs = inject(DialogService);

  readonly grants = signal<StorageGrant[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly requesting = signal(false);
  readonly submitting = signal(false);
  /** The grant being withdrawn, so a second click can't send a second request. */
  readonly withdrawing = signal<number | null>(null);
  readonly bucket = signal('');
  readonly prefix = signal('');
  readonly note = signal('');

  readonly location = grantLocation;
  readonly pendingCount = computed(() => this.grants().filter((g) => g.status === 'Pending').length);
  readonly atPendingLimit = computed(() => this.pendingCount() >= MAX_PENDING_STORAGE_GRANTS);
  readonly bucketProblem = computed(() => {
    const bucket = this.bucket().trim();
    if (!bucket) return null;
    return isValidGcsBucketName(bucket)
      ? null
      : 'Use the bucket name only: 3-63 lowercase letters, digits, dashes, underscores or dots.';
  });
  readonly canSubmit = computed(
    () => !!this.bucket().trim() && !this.bucketProblem() && !this.submitting()
  );

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      // Another project: its predecessor's error and open form don't apply.
      untracked(() => {
        this.error.set(null);
        this.requesting.set(false);
      });
      void this.load(projectId);
    });
  }

  async load(projectId: number | null = this.projectId()): Promise<void> {
    if (projectId === null) return;
    this.loading.set(true);
    // A response for a project this view has since left is dropped.
    const current = () => this.projectId() === projectId;
    try {
      const grants = await this.#service.list(projectId);
      if (current()) this.grants.set(grants);
    } catch (err) {
      if (current()) this.error.set(messageOf(err, 'Failed to load storage access'));
    } finally {
      if (current()) this.loading.set(false);
    }
  }

  canWithdraw(grant: StorageGrant): boolean {
    return LIVE.includes(grant.status);
  }

  startRequest(): void {
    this.error.set(null);
    this.bucket.set('');
    this.prefix.set('');
    this.note.set('');
    this.requesting.set(true);
  }

  cancelRequest(): void {
    this.requesting.set(false);
  }

  async submitRequest(): Promise<void> {
    const projectId = this.projectId();
    if (projectId === null || !this.canSubmit()) return;
    this.submitting.set(true);
    this.error.set(null);
    try {
      await this.#service.request(projectId, {
        provider: ObjectStorageProvider.GCS,
        bucket: this.bucket().trim(),
        prefix: this.prefix().trim() || undefined,
        note: this.note().trim() || undefined,
      });
      this.requesting.set(false);
      await this.load(projectId);
    } catch (err) {
      this.error.set(messageOf(err, 'Failed to request storage access'));
    } finally {
      this.submitting.set(false);
    }
  }

  async withdraw(grant: StorageGrant): Promise<void> {
    const projectId = this.projectId();
    if (projectId === null || this.withdrawing() !== null) return;
    // Claimed before the confirmation, so a second click can't open a second
    // one even if the dialog isn't modal.
    this.withdrawing.set(grant.storageGrantId);
    const approved = grant.status === 'Approved';
    const confirmed = await firstValueFrom(
      this.#dialogs.confirm({
        title: approved ? 'Give up storage access' : 'Withdraw request',
        message: approved
          ? `Give up access to ${grantLocation(grant)}? Connections using it stop working.`
          : `Withdraw the request for ${grantLocation(grant)}?`,
        confirmCaption: approved ? 'Give up access' : 'Withdraw',
        cancelCaption: 'Cancel',
        isDestructive: true,
      }),
      // A dialog that closes without an answer counts as a cancel, so the guard is released.
      { defaultValue: false }
    );
    if (!confirmed) {
      this.withdrawing.set(null);
      return;
    }
    this.error.set(null);
    try {
      await this.#service.withdraw(projectId, grant.storageGrantId);
    } catch (err) {
      this.error.set(messageOf(err, 'Failed to withdraw'));
    } finally {
      this.withdrawing.set(null);
    }
    await this.load(projectId);
  }
}

function messageOf(err: unknown, fallback: string): string {
  const message = (err as { error?: { message?: unknown } } | null)?.error?.message;
  return typeof message === 'string' && message ? message : fallback;
}
